import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './css/index.module.scss';
import Button from '../../../../ui-kit/Button';
import useTranslations from '../../../../hooks/useTranslations';
import { DICTIONARY } from '../../../../translations/dictionary';
import useProjects from '../../../../hooks/useProjects';
import Checkbox from '../../../../ui-kit/Checkbox';
import Loading from '../../../../ui-kit/Loading';
import Api from '../../../../api';
import sendRequest from '../../../../helpers/sendRequest';
import MiddleEllipsisText from '../../../../components/MiddleEllipsisText';

const sameId = (left, right) => String(left) === String(right);

// Attach creates copies and does not write project membership back onto the history configuration.
// Preselect only what this payload already has. configuration.projects is optional; project_id is the fallback.
// The same project is kept once when several configurations point at it.
const getConfigurationProjectIds = (configurations) => {
  const ids = [];

  configurations.forEach((configuration) => {
    if (Array.isArray(configuration?.projects)) {
      configuration.projects.forEach((project) => {
        const id = project?.id ?? project;
        if (id) ids.push(id);
      });
    }

    if (configuration?.project_id) ids.push(configuration.project_id);
  });

  return ids.filter((id, index) => ids.findIndex((item) => sameId(item, id)) === index);
};

const placeConfigurationProjectsFirst = (list, pinnedIds) => {
  const unique = [];
  list.forEach((project) => {
    if (project?.id == null) return;
    if (unique.some((item) => sameId(item.id, project.id))) return;
    unique.push(project);
  });

  const pinned = pinnedIds
    .map((id) => unique.find((project) => sameId(project.id, id)))
    .filter(Boolean);
  const rest = unique.filter((project) => !pinned.some((item) => sameId(item.id, project.id)));
  return [...pinned, ...rest];
};

function AddToProjectModal({ onSave, configurations = [] }) {
  const { t } = useTranslations();
  const { onNewProjectClick } = useProjects();
  const configurationProjectIds = useMemo(
    () => getConfigurationProjectIds(configurations),
    [configurations],
  );
  const configurationProjectIdsRef = useRef(configurationProjectIds);
  configurationProjectIdsRef.current = configurationProjectIds;

  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState(configurationProjectIds);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const listRef = useRef(null);
  const loadingRef = useRef(false);
  const pageRef = useRef(0);
  const lastPageRef = useRef(1);

  const requestProjectsPage = async (page) => {
    const data = await sendRequest({
      request: Api.getProjects,
      payload: { page, items_on_page: 20 },
      showErrorInModal: false,
      warnErrorText: 'while getting projects',
    });
    if (data?.status !== 'ok') return null;

    const nextProjects = data.data.projects || [];
    return {
      projects: nextProjects,
      lastPage: nextProjects.length ? (data.data.pages?.last_page || page) : page,
    };
  };

  const loadNextPage = async () => {
    if (loadingRef.current) return;
    const nextPage = pageRef.current + 1;
    if (pageRef.current > 0 && nextPage > lastPageRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    const result = await requestProjectsPage(nextPage);
    loadingRef.current = false;
    setLoading(false);
    if (!result) return;

    setProjects((prev) => placeConfigurationProjectsFirst(
      [...prev, ...result.projects],
      configurationProjectIdsRef.current,
    ));
    pageRef.current = nextPage;
    lastPageRef.current = result.lastPage;
  };

  // History configurations do not include the projects that already hold their copies.
  // Pages are requested one by one only until every project id already on the payload is in the list.
  const loadUntilConfigurationProjects = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    let page = 1;
    let lastPage = 1;
    let loaded = [];
    const needed = configurationProjectIdsRef.current;

    try {
      while (page <= lastPage) {
        const result = await requestProjectsPage(page);
        if (!result) break;

        loaded = placeConfigurationProjectsFirst([...loaded, ...result.projects], needed);
        setProjects(loaded);
        lastPage = result.lastPage;
        pageRef.current = page;
        lastPageRef.current = lastPage;

        const foundAll = needed.every((id) => loaded.some((project) => sameId(project.id, id)));
        if (foundAll) break;
        page += 1;
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUntilConfigurationProjects().then();
  }, []);

  useEffect(() => {
    const list = listRef.current;
    if (!list || loading) return;
    if (pageRef.current >= lastPageRef.current) return;
    if (list.scrollHeight <= list.clientHeight + 1) {
      loadNextPage().then();
    }
  }, [projects, loading]);

  const onListScroll = (event) => {
    const list = event.currentTarget;
    const distance = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distance > 40) return;
    if (pageRef.current >= lastPageRef.current) return;
    loadNextPage().then();
  };

  const onProjectClick = (id) => {
    setSelectedIds((prev) => (
      prev.some((item) => sameId(item, id))
        ? prev.filter((item) => !sameId(item, id))
        : [...prev, id]
    ));
  };

  const onCreateProject = () => {
    onNewProjectClick({
      cb: (project) => {
        if (!project?.id) return;
        setProjects((prev) => placeConfigurationProjectsFirst(
          [project, ...prev],
          configurationProjectIdsRef.current,
        ));
        setSelectedIds((prev) => (
          prev.some((item) => sameId(item, project.id)) ? prev : [project.id, ...prev]
        ));
      },
    });
  };

  const onSaveClick = async () => {
    if (!selectedIds.length || saving) return;
    const selectedProjects = [];
    selectedIds.forEach((id) => {
      const project = projects.find((item) => sameId(item.id, id));
      if (project && !selectedProjects.some((item) => sameId(item.id, project.id))) {
        selectedProjects.push(project);
      }
    });
    setSaving(true);
    await onSave(selectedProjects);
    setSaving(false);
  };

  return (
    <div className={styles.addToProjectModal}>
      <p className={styles.title}>{t(DICTIONARY.addToProject)}</p>
      <p className={styles.adding}>{t(DICTIONARY.addingConfigurations, configurations.length)}</p>
      <div className={styles.newProject}>
        <Button
          text={t(DICTIONARY.createNewProject)}
          onClick={onCreateProject}
          height={34}
          paddingHorizontal={16}
        />
      </div>
      <div className={styles.list} ref={listRef} onScroll={onListScroll}>
        {projects.map((project) => (
          <div
            key={project.id}
            className={styles.row}
            onClick={() => onProjectClick(project.id)}
          >
            <Checkbox value={selectedIds.some((id) => sameId(id, project.id))} />
            <div className={styles.rowText}>
              <p className={styles.count}>
                {project.configurations_count}
                {' '}
                {t(DICTIONARY.configurations, project.configurations_count)}
              </p>
              <MiddleEllipsisText className={styles.name} text={project.name} />
            </div>
          </div>
        ))}
        {loading ? <Loading height={22} width={22} /> : null}
        {!loading && !projects.length ? <p className={styles.empty}>{t(DICTIONARY.empty)}</p> : null}
      </div>
      <Button
        width="100%"
        text={t(DICTIONARY.add)}
        onClick={onSaveClick}
        isDisabled={!selectedIds.length || saving || !configurations.length}
      />
    </div>
  );
}

export default AddToProjectModal;
